import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import CustomerHeader from "../../components/CustomerHeader";
import ExperienceMap from "../../components/ExperienceMap";
import ExperienceRealMap from "../../components/ExperienceRealMap";
import ServiceReviewDialog from "../../components/ServiceReviewDialog";
import { Button } from "../../components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import { Sparkles, ChevronLeft, Loader2, Clock, Check, ChefHat, Bike, CheckCircle2, XCircle, MapPin, Gem, Sparkle, CalendarClock, Send, Play, Star, Share2, Copy, X, Users } from "lucide-react";

const ICONS = { clock: Clock, check: Check, chef: ChefHat, bike: Bike, done: CheckCircle2, x: XCircle, send: Send, play: Play };
const LABEL_COLOR = { "Completata": "#00E676", "In corso": "#22D3EE", "In preparazione": "#A78BFA", "Confermata": "#60A5FA", "In attesa": "#F59E0B", "Annullata": "#EF4444" };

export default function ExperienceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exp, setExp] = useState(null);
  const [err, setErr] = useState(false);
  const [reviewFor, setReviewFor] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/experiences/orders/${id}`).then(({ data }) => setExp(data)).catch(() => setErr(true));
  }, [id]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  if (err) return (<div className="min-h-screen bg-[#0A0A0C] text-white"><CustomerHeader /><div className="text-center py-20 text-zinc-500">Esperienza non trovata.</div></div>);
  if (!exp) return (<div className="min-h-screen bg-[#0A0A0C] text-white"><CustomerHeader /><div className="text-center py-20 text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /> Caricamento…</div></div>);

  const oc = LABEL_COLOR[exp.overall_label] || "#7DF9FF";
  const hasGeo = (exp.steps || []).some((s) => typeof s.lat === "number" && typeof s.lng === "number" && !s.online);
  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white">
      <CustomerHeader />
      <main className="max-w-2xl mx-auto px-4 py-5" data-testid="experience-detail-page">
        <button onClick={() => navigate("/app/esperienze")} className="flex items-center gap-1.5 text-zinc-400 hover:text-white mb-3 text-sm" data-testid="exp-detail-back"><ChevronLeft className="h-4 w-4" /> Le mie Esperienze</button>

        <div className="rounded-2xl overflow-hidden border border-white/10 relative">
          <div className="h-40 bg-cover bg-center" style={{ backgroundImage: `url(${exp.hero_image})` }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/50 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#7DF9FF]" />
              <h1 className="font-display font-black text-xl tracking-tight">{exp.title}</h1>
            </div>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${oc}22`, color: oc, border: `1px solid ${oc}66` }} data-testid="exp-overall-label">{exp.overall_label}</span>
              <span className="text-xs text-zinc-400">{exp.n_steps} tappe</span>
              <button onClick={() => setShareOpen(true)} data-testid="exp-invite-btn" className="ml-auto text-xs flex items-center gap-1 rounded-full bg-[#7DF9FF]/15 text-[#7DF9FF] border border-[#7DF9FF]/40 px-3 py-1.5 font-semibold"><Users className="h-3.5 w-3.5" /> Invita amici</button>
            </div>
          </div>
        </div>

        {shareOpen && <ShareDialog eid={id} title={exp.title} onClose={() => setShareOpen(false)} />}

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-zinc-400 mb-1"><span>Avanzamento esperienza</span><span>{exp.overall_progress}%</span></div>
          <div className="h-3 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${exp.overall_progress}%`, background: `linear-gradient(90deg, #7DF9FF, ${oc})` }} data-testid="exp-overall-progress" />
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center gap-1.5 mb-2"><Sparkles className="h-4 w-4 text-[#7DF9FF]" /><span className="font-semibold text-sm">Il tuo itinerario</span></div>
          {hasGeo
            ? <ExperienceRealMap steps={exp.steps || []} onOpen={(s) => { if (s.order_id) navigate(`/app/orders/${s.order_id}`); }} />
            : <ExperienceMap steps={exp.steps || []} allCompleted={exp.overall_label === "Completata"} onOpen={(s) => { if (s.order_id) navigate(`/app/orders/${s.order_id}`); }} />}
        </div>

        <div className="mt-6 relative pl-6">
          <div className="absolute left-[9px] top-2 bottom-2 w-px bg-white/10" />
          {(exp.steps || []).map((s, i) => {
            const Icon = ICONS[s.step_icon] || Clock;
            const isService = s.kind === "service";
            const respond = async (accept) => {
              try {
                await api.post(`/experiences/service-bookings/${s.booking_id}/respond`, { accept });
                toast.success(accept ? "Nuovo orario accettato" : "Prenotazione annullata");
                load();
              } catch (e) { toast.error("Operazione non riuscita"); }
            };
            return (
              <div key={s.ref_id || s.order_id || i} className="relative mb-4" data-testid={`exp-step-${i}`}>
                <div className="absolute -left-6 top-1 h-[19px] w-[19px] rounded-full flex items-center justify-center" style={{ background: `${s.step_color}22`, border: `2px solid ${s.step_color}` }}>
                  {isService ? <Sparkle className="h-3 w-3" style={{ color: s.step_color }} /> : <Icon className="h-3 w-3" style={{ color: s.step_color }} />}
                </div>
                <div className="rounded-xl bg-[#141417] border border-white/10 p-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm flex items-center gap-1.5">
                      {isService && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#7DF9FF]/15 text-[#7DF9FF] border border-[#7DF9FF]/30">Servizio</span>}
                      {s.business_name}
                    </div>
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0" style={{ background: `${s.step_color}1f`, color: s.step_color, border: `1px solid ${s.step_color}55` }} data-testid={`exp-step-status-${i}`}>{s.step_label}</span>
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">{isService ? s.title : (s.items || []).map((it) => `${it.qty}× ${it.name}`).join(" · ")}</div>
                  {isService && s.booking_datetime && <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {String(s.booking_datetime).replace("T", " · ")}</div>}
                  {isService && s.proposed_datetime && (
                    <div className="mt-2 rounded-lg bg-[#60A5FA]/10 border border-[#60A5FA]/30 px-3 py-2" data-testid={`exp-step-proposed-${i}`}>
                      <div className="text-xs text-[#93C5FD] flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Nuovo orario proposto: {String(s.proposed_datetime).replace("T", " · ")}</div>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => respond(true)} data-testid={`exp-accept-proposed-${i}`} className="text-xs rounded-md bg-[#00E676] text-black px-3 py-1 font-semibold">Accetta</button>
                        <button onClick={() => respond(false)} data-testid={`exp-decline-proposed-${i}`} className="text-xs rounded-md bg-white/10 text-zinc-300 px-3 py-1">Rifiuta</button>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500 flex-wrap">
                    <span className="text-[#00E676] font-semibold">{Number(s.total) > 0 ? `€${Number(s.total).toFixed(2)}` : "Preventivo"}</span>
                    {s.duration_min ? <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> ≈ {s.duration_min} min</span> : null}
                    {!isService && s.rider_name && s.step_key === "consegna" ? <span className="flex items-center gap-1 text-[#22D3EE]"><Bike className="h-3 w-3" /> {s.rider_name}</span> : null}
                    {isService && s.refunded ? <span className="text-[#EF4444]">Rimborsato in crediti</span> : null}
                    {isService && s.step_key === "completata" && (s.reviewed
                      ? <span className="ml-auto text-[#FFD700] flex items-center gap-1" data-testid={`exp-reviewed-${i}`}><Star className="h-3 w-3 fill-[#FFD700]" /> Recensito</span>
                      : <button onClick={() => setReviewFor(s)} data-testid={`exp-review-btn-${i}`} className="ml-auto text-[#FFD700] hover:underline flex items-center gap-1"><Star className="h-3 w-3" /> Lascia una recensione</button>)}
                    {!isService && <button onClick={() => navigate(`/app/orders/${s.order_id}`)} className="ml-auto text-[#7DF9FF] hover:underline" data-testid={`exp-step-track-${i}`}>Traccia</button>}
                  </div>
                </div>
              </div>
            );
          })}
          {(exp.steps || []).length === 0 && <div className="text-sm text-zinc-500">Nessuna tappa registrata.</div>}
        </div>

        <div className="mt-4 rounded-2xl bg-[#141417] border border-white/10 p-4" data-testid="exp-summary">
          <div className="font-semibold mb-2">Riepilogo</div>
          <div className="flex items-center gap-2 text-sm text-zinc-400"><MapPin className="h-4 w-4" /> {exp.address}</div>
          <div className="flex items-center justify-between mt-3 text-sm">
            <span className="text-zinc-400">Totale esperienza</span>
            <span className="font-bold">€{Number(exp.exp_total || 0).toFixed(2)}</span>
          </div>
          {exp.credit_discount > 0 && (
            <div className="flex items-center justify-between text-sm text-[#C4B5FD]"><span className="flex items-center gap-1"><Gem className="h-3.5 w-3.5" /> Crediti usati</span><span>-€{Number(exp.credit_discount).toFixed(2)}</span></div>
          )}
          <div className="flex items-center justify-between mt-1 text-sm border-t border-white/10 pt-2">
            <span className="text-zinc-400">Pagato</span>
            <span className="font-bold text-[#00E676]">€{Number(exp.amount ?? exp.exp_total ?? 0).toFixed(2)}</span>
          </div>
        </div>
      </main>
      {reviewFor && <ServiceReviewDialog bookingId={reviewFor.booking_id} title={`${reviewFor.business_name} · ${reviewFor.title}`} onClose={() => setReviewFor(null)} onDone={() => { setReviewFor(null); load(); }} />}
    </div>
  );
}

function ShareDialog({ eid, title, onClose }) {
  const [state, setState] = useState({ loading: true, token: null, guests: [], summary: {} });
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/experiences/orders/${eid}/share`);
      if (data.share_token) setState({ loading: false, token: data.share_token, guests: data.guests || [], summary: data.summary || {} });
      else { const { data: s } = await api.post(`/experiences/orders/${eid}/share`); setState({ loading: false, token: s.share_token, guests: [], summary: {} }); }
    } catch { setState((p) => ({ ...p, loading: false })); }
  }, [eid]);
  useEffect(() => { load(); }, [load]);

  const link = state.token ? `${window.location.origin}/esperienze/condivisa/${state.token}` : "";
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Link copiato!"); };
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title, url: link }); } catch { } } else copy();
  };
  const revoke = async () => { await api.post(`/experiences/orders/${eid}/share/revoke`); toast.success("Invito revocato"); onClose(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141417] border border-white/10 rounded-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="share-dialog">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold text-lg flex items-center gap-2"><Users className="h-5 w-5 text-[#7DF9FF]" /> Invita amici</div>
          <button onClick={onClose} data-testid="share-close"><X className="h-5 w-5 text-zinc-400" /></button>
        </div>
        {state.loading ? <div className="py-8 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
          <>
            <div className="bg-white rounded-xl p-3 flex items-center justify-center" data-testid="share-qr">
              {link && <QRCodeCanvas value={link} size={168} includeMargin />}
            </div>
            <div className="mt-3 flex items-center gap-2 bg-[#0A0A0C] border border-white/10 rounded-lg px-3 py-2">
              <input readOnly value={link} className="bg-transparent text-xs text-zinc-400 flex-1 outline-none" data-testid="share-link" />
              <button onClick={copy} data-testid="share-copy" className="text-[#7DF9FF] shrink-0"><Copy className="h-4 w-4" /></button>
            </div>
            <div className="flex gap-2 mt-3">
              <Button data-testid="share-native" onClick={nativeShare} className="flex-1 bg-[#00E676] text-black rounded-full font-semibold"><Share2 className="h-4 w-4 mr-1" /> Condividi</Button>
              <Button data-testid="share-revoke" onClick={revoke} variant="outline" className="rounded-full border-white/15 text-zinc-300">Revoca</Button>
            </div>
            <p className="text-xs text-zinc-500 mt-3">Il link scade tra 14 giorni. Nessun indirizzo privato viene mostrato agli ospiti.</p>
            <div className="mt-3 pt-3 border-t border-white/10" data-testid="share-summary">
              <div className="text-xs text-zinc-400 mb-2">Stato partecipanti</div>
              <div className="flex flex-wrap gap-2 mb-2">
                <span className="text-xs rounded-full px-2.5 py-1 bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30">✓ {state.summary.accepted || 0} accettati</span>
                <span className="text-xs rounded-full px-2.5 py-1 bg-[#22D3EE]/15 text-[#22D3EE] border border-[#22D3EE]/30">🎊 {state.summary.entered || 0} entrati</span>
                <span className="text-xs rounded-full px-2.5 py-1 bg-zinc-500/15 text-zinc-400 border border-zinc-500/30">✕ {state.summary.declined || 0} rifiutati</span>
                <span className="text-xs rounded-full px-2.5 py-1 bg-red-500/10 text-red-400 border border-red-500/20">↩ {state.summary.cancelled || 0} annullati</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {state.guests.filter((g) => ["accepted", "entered"].includes(g.status)).map((g, i) => (
                  <span key={i} data-testid={`share-guest-${i}`} className="text-xs rounded-full px-2.5 py-1 bg-white/5 border border-white/15 flex items-center gap-1">{g.status === "entered" ? "🎊" : "✓"} {g.name}</span>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
